import { api } from '@/lib/api'
import type {
  ActionType,
  AddCommentInput,
  Board,
  BoardColumnDto,
  ChangeAssigneeInput,
  Comment,
  ContextBoardKey,
  CreateActionTypeInput,
  CreateBoardColumnInput,
  CreateDocumentTypeInput,
  CreateTaskInput,
  DocumentType,
  Task,
  TaskContext,
  TaskFile,
  TaskStatus,
  UpdateActionTypeInput,
  UpdateBoardColumnInput,
  UpdateDocumentTypeInput,
  UpdateFileMetadataInput,
  UploadFileInput,
} from './types'

/** Ścieżki /api/tasks trafiają do proxy w easycrm backend, który forwarduje do halocrm-tasks. */

export async function listMyTasks(status?: TaskStatus) {
  const params = new URLSearchParams({ assignee: 'me' })
  if (status) params.set('status', status)
  const r = await api.get<Task[]>(`/api/tasks?${params.toString()}`)
  return r.data
}

export async function getTask(id: number) {
  const r = await api.get<Task>(`/api/tasks/${id}`)
  return r.data
}

export async function createTask(input: CreateTaskInput) {
  const r = await api.post<Task>('/api/tasks', input)
  return r.data
}

export async function changeTaskStatus(id: number, status: TaskStatus) {
  const r = await api.patch<Task>(`/api/tasks/${id}/status`, { status })
  return r.data
}

// Faza 2A - delegacja
export async function claimTask(id: number): Promise<Task> {
  const r = await api.post<Task>(`/api/tasks/${id}/claim`)
  return r.data
}

export async function unclaimTask(id: number): Promise<Task> {
  const r = await api.delete<Task>(`/api/tasks/${id}/claim`)
  return r.data
}

export async function changeAssignee(id: number, input: ChangeAssigneeInput): Promise<Task> {
  const r = await api.post<Task>(`/api/tasks/${id}/assignee`, input)
  return r.data
}

// Faza 2B - boards

export async function getPersonalBoard(): Promise<Board> {
  const r = await api.get<Board>('/api/boards/personal')
  return r.data
}

export async function getContextBoard(key: ContextBoardKey): Promise<Board> {
  const r = await api.get<Board>(
    `/api/boards/context/${key.system}/${key.type}/${key.id}`,
    { params: { label: key.label } },
  )
  return r.data
}

export async function createBoardColumn(boardId: number, input: CreateBoardColumnInput): Promise<BoardColumnDto> {
  const r = await api.post<BoardColumnDto>(`/api/boards/${boardId}/columns`, input)
  return r.data
}

export async function updateBoardColumn(boardId: number, columnId: number, patch: UpdateBoardColumnInput): Promise<BoardColumnDto> {
  const r = await api.patch<BoardColumnDto>(`/api/boards/${boardId}/columns/${columnId}`, patch)
  return r.data
}

export async function deleteBoardColumn(boardId: number, columnId: number, moveCardsToColumnId?: number): Promise<void> {
  await api.delete(`/api/boards/${boardId}/columns/${columnId}`, {
    data: moveCardsToColumnId != null ? { moveCardsToColumnId } : undefined,
  })
}

export async function moveCard(boardId: number, taskId: number, columnId: number, position: number): Promise<Board> {
  const r = await api.patch<Board>(`/api/boards/${boardId}/cards/${taskId}`, { columnId, position })
  return r.data
}

// Faza 2C - comments

export async function listComments(taskId: number): Promise<Comment[]> {
  const r = await api.get<Comment[]>(`/api/tasks/${taskId}/comments`)
  return r.data
}

export async function addComment(taskId: number, input: AddCommentInput): Promise<Comment> {
  const r = await api.post<Comment>(`/api/tasks/${taskId}/comments`, input)
  return r.data
}

// Faza 2D - pliki zadań

export async function listTaskFiles(taskId: number): Promise<TaskFile[]> {
  const r = await api.get<TaskFile[]>(`/api/tasks/${taskId}/files`)
  return r.data
}

export async function uploadTaskFile(taskId: number, input: UploadFileInput): Promise<TaskFile> {
  const formData = new FormData()
  formData.append('file', input.file)
  if (input.description) formData.append('description', input.description)
  if (input.documentTypeCode) formData.append('documentTypeCode', input.documentTypeCode)
  const r = await api.post<TaskFile>(`/api/tasks/${taskId}/files`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return r.data
}

export async function deleteTaskFile(taskId: number, fileId: number): Promise<void> {
  await api.delete(`/api/tasks/${taskId}/files/${fileId}`)
}

export async function updateTaskFile(taskId: number, fileId: number, patch: UpdateFileMetadataInput): Promise<TaskFile> {
  const r = await api.patch<TaskFile>(`/api/tasks/${taskId}/files/${fileId}`, patch)
  return r.data
}

/** Returns an absolute URL the browser can fetch directly (redirects to S3 presigned URL).
 *  Use this for `<a download href=...>` and similar - browser follows the 302. NOT for
 *  `<img src=...>` (cross-origin + credentials breaks the redirect follow). */
export function getDownloadHref(taskId: number, fileId: number): string {
  return `/api/tasks/${taskId}/files/${fileId}/download`
}

/** Fetches the presigned download URL as JSON. Use this when you need the URL as a string
 *  (e.g. lightbox `<img src>` - 302 follow doesn't work cross-origin with credentials). */
export async function getDownloadUrl(taskId: number, fileId: number): Promise<{ url: string }> {
  const r = await api.get<{ url: string }>(`/api/tasks/${taskId}/files/${fileId}/download-url`)
  return r.data
}

/** Fetches the presigned thumbnail URL as JSON. Symmetric to getDownloadUrl. */
export async function getThumbnailUrl(taskId: number, fileId: number): Promise<{ url: string }> {
  const r = await api.get<{ url: string }>(`/api/tasks/${taskId}/files/${fileId}/thumbnail-url`)
  return r.data
}

export async function listTaskDocumentTypes(): Promise<DocumentType[]> {
  const r = await api.get<DocumentType[]>('/api/task-document-types')
  return r.data
}

export async function createTaskDocumentType(input: CreateDocumentTypeInput): Promise<DocumentType> {
  const r = await api.post<DocumentType>('/api/task-document-types', input)
  return r.data
}

export async function updateTaskDocumentType(code: string, patch: UpdateDocumentTypeInput): Promise<DocumentType> {
  const r = await api.patch<DocumentType>(`/api/task-document-types/${encodeURIComponent(code)}`, patch)
  return r.data
}

export async function deactivateTaskDocumentType(code: string): Promise<void> {
  await api.delete(`/api/task-document-types/${encodeURIComponent(code)}`)
}

// Faza 2E - typy akcji

export async function listTaskActionTypes(): Promise<ActionType[]> {
  const r = await api.get<ActionType[]>('/api/task-action-types')
  return r.data
}

export async function createTaskActionType(input: CreateActionTypeInput): Promise<ActionType> {
  const r = await api.post<ActionType>('/api/task-action-types', input)
  return r.data
}

export async function updateTaskActionType(code: string, patch: UpdateActionTypeInput): Promise<ActionType> {
  const r = await api.patch<ActionType>(`/api/task-action-types/${encodeURIComponent(code)}`, patch)
  return r.data
}

export async function deactivateTaskActionType(code: string): Promise<void> {
  await api.delete(`/api/task-action-types/${encodeURIComponent(code)}`)
}

/** Changes action type of a task. Pass null to clear. */
export async function changeTaskActionType(taskId: number, actionTypeCode: string | null): Promise<Task> {
  const r = await api.patch<Task>(`/api/tasks/${taskId}/action-type`, { actionTypeCode })
  return r.data
}

// Faza 2H-B - zmiana kontekstu

/** Changes (or clears) the context of a task. Pass null to detach the task from its contract/lead/meeting. */
export async function changeTaskContext(taskId: number, context: TaskContext | null): Promise<Task> {
  const r = await api.patch<Task>(`/api/tasks/${taskId}/context`, { context })
  return r.data
}
